import type { ComponentType, ReactNode, SVGProps } from 'react';
import { Link } from 'react-router';
import type { Renewal, Service } from '@bekebi/contracts/api';
import {
  SERVICE_TYPES,
  SERVICE_TYPE_LABELS,
  formatStorageSize,
  hasServiceHealth,
  storagePercent,
} from '@bekebi/domain';
import { Button } from '../../components/button';
import {
  AlertIcon,
  BankIcon,
  BucketIcon,
  CalendarIcon,
  CheckCircleIcon,
  HelpCircleIcon,
  HistoryIcon,
  LayersIcon,
  ListIcon,
  MailIcon,
  PlugIcon,
  PulseIcon,
  ServicesIcon,
} from '../../components/icons';
import { LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { useActivity, useUserDirectory } from '../activity/api';
import { actionLabel, actorKindLabel } from '../activity/present';
import { usePaymentAccount } from '../payment-account/api';
import { useUpcomingRenewals } from '../renewals/api';
import { RenewalStateBadge, formatMoney } from '../renewals/present';
import {
  MAILBOX_CAPABILITY_TEXT,
  useBackupStatus,
  useMailboxCapability,
  useServiceMetrics,
  useServices,
} from '../services/api';
import { BackupStateBadge, backupStateSentence } from '../services/backup-status';
import { formatBytes } from '../mailboxes/quota';
import { HealthBadge, ServiceTypeIcon } from '../services/components';
import {
  CHECK_KIND_TITLES,
  CHECK_STATUS_LABELS,
  HEALTH_REASON_TEXT,
  formatDate,
  formatDateTime,
  formatRelative,
} from '../services/format';
import { useAlerts, useNotificationState, useWorkerStatus } from '../monitoring/api';
import { useAllChecks } from './api';

/**
 * Admin Dashboard (/app/dashboard): where the owner starts. Built only from existing endpoints and
 * shown exactly as the API reports it — service health and its reason, check status and staleness,
 * renewal condition and countdown, whether the Payment Account is set, recent activity. Nothing is
 * scored, ranked, estimated or charted, and what the portal cannot see is said plainly.
 */
export function Dashboard() {
  return (
    <div className="space-y-6">
      <DashboardHeader />
      <StatusOverview />
      <AttentionSection />
      <ServicesSection />
      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <RenewalsSection />
        <RecentActivitySection />
        <PaymentAccountPanel view="admin" />
        <BackupsPanel />
      </div>
      <NotConnectedSection />
    </div>
  );
}

export const linkClass =
  'text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link';

// ---------------------------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------------------------

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

/** A dashboard panel: a compact title row with an optional "view all" link, then its content. */
export function Panel({
  title,
  icon: TitleIcon,
  action,
  children,
}: {
  title: string;
  icon?: IconType;
  action?: ReactNode;
  children: ReactNode;
}) {
  const id = `panel-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <section
      aria-labelledby={id}
      className="flex flex-col rounded-[var(--radius-card)] border border-line bg-surface p-5 sm:p-6"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id={id} className="flex items-center gap-2.5 text-[0.95rem]">
          {TitleIcon ? <TitleIcon className="size-[1.1rem] text-muted" /> : null}
          {title}
        </h2>
        {action}
      </div>
      <div className="mt-4 flex-1">{children}</div>
    </section>
  );
}

/** "All services →" — a quiet link to the full page. */
export function ViewAllLink({
  to,
  children,
  short,
}: {
  to: string;
  children: string;
  short?: string;
}) {
  return (
    <Link
      to={to}
      aria-label={short ? children : undefined}
      className="inline-flex shrink-0 items-center gap-1 rounded-md text-sm font-medium text-accent-link hover:underline hover:underline-offset-4"
    >
      {short ?? children}
      <span aria-hidden="true">→</span>
    </Link>
  );
}

/** A designed empty state for a panel: icon, short heading, one line. */
export function PanelEmpty({
  icon: EmptyIcon,
  title,
  children,
  tone = 'neutral',
  action,
}: {
  icon: IconType;
  title: string;
  children?: ReactNode;
  tone?: 'neutral' | 'success';
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-line px-4 py-6 text-center">
      <span
        className={`flex size-10 items-center justify-center rounded-xl ${tone === 'success' ? 'bg-success-soft text-success' : 'bg-raised text-muted'}`}
      >
        <EmptyIcon className="size-5" />
      </span>
      <p className="mt-3 text-sm font-semibold text-fg">{title}</p>
      {children ? <p className="mt-1 max-w-sm text-sm text-muted">{children}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-danger-line bg-danger-soft px-4 py-3 text-sm text-fg"
    >
      <p>{message}</p>
      <Button variant="secondary" className="mt-3" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function greeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** The page name (the h1) as a quiet label, under it a friendly greeting. */
export function DashboardHeader() {
  return (
    <div>
      <h1 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted sm:text-xs">
        Dashboard
      </h1>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-fg sm:text-3xl">{greeting()}</p>
      <p className="mt-1.5 text-sm text-muted sm:text-base">
        Here&rsquo;s what&rsquo;s happening across Bekebi&rsquo;s services.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Status overview — the services' health counted exactly as the API reports it
// ---------------------------------------------------------------------------------------------

const names = (list: Service[]) => list.map((s) => s.name).join(', ');

/**
 * One plain sentence and three counts, built only from each service's API health (healthy / needs
 * attention / unknown). No score, no percentage. Unknown services are named — unknown is never
 * shown as healthy. When the services cannot be read it says nothing rather than guessing (the
 * Services panel shows the error and a retry).
 */
export function StatusOverview() {
  const services = useServices();
  if (services.isPending) return <LoadingState label="Checking the services…" />;
  if (services.isError) return null;
  // The Storage Bucket has no health state (director, 2026-11-02): it is not counted here.
  const all = services.data.filter((s) => hasServiceHealth(s.type));
  const by = (state: Service['health']['state']) => all.filter((s) => s.health.state === state);
  const healthy = by('healthy');
  const attention = by('needs_attention');
  const unknown = by('unknown');
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

  let tone: 'warning' | 'success' | 'neutral' = 'neutral';
  let headline: string;
  let detail: string;
  if (all.length === 0) {
    headline = 'No services have been added yet.';
    detail = 'Their health will appear here once they are added.';
  } else if (attention.length > 0) {
    tone = 'warning';
    headline = `${plural(attention.length, 'service needs', 'services need')} attention.`;
    detail = `Needs attention: ${names(attention)}.${unknown.length > 0 ? ` Not known yet: ${names(unknown)}.` : ''}`;
  } else if (unknown.length === all.length) {
    headline = 'Health is not known yet.';
    detail = `There are no current check results for ${names(unknown)}. Unknown is not the same as healthy.`;
  } else if (unknown.length > 0) {
    headline = `${plural(healthy.length, 'service is', 'services are')} healthy.`;
    detail = `Not known yet: ${names(unknown)} — no current check results. Unknown is not the same as healthy.`;
  } else {
    tone = 'success';
    headline = 'All services are healthy.';
    detail = "Based on each service's latest check.";
  }

  const TONE = {
    warning: { icon: AlertIcon, ring: 'border-warning-line', tile: 'bg-warning-soft text-warning' },
    success: {
      icon: CheckCircleIcon,
      ring: 'border-success-line',
      tile: 'bg-success-soft text-success',
    },
    neutral: { icon: HelpCircleIcon, ring: 'border-line', tile: 'bg-unknown-soft text-unknown' },
  }[tone];
  const ToneIcon = TONE.icon;
  const counts: [string, number, string][] = [
    ['Healthy', healthy.length, 'bg-success'],
    ['Needs attention', attention.length, 'bg-warning'],
    ['Unknown', unknown.length, 'bg-unknown'],
  ];

  return (
    <section
      aria-label="Service status overview"
      className={`flex flex-col gap-4 rounded-[var(--radius-card)] border ${TONE.ring} bg-surface p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between`}
    >
      <div className="flex min-w-0 items-center gap-4">
        <span
          className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${TONE.tile}`}
        >
          <ToneIcon className="size-[1.4rem]" />
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-fg sm:text-lg">{headline}</p>
          <p className="mt-0.5 text-sm text-muted">{detail}</p>
        </div>
      </div>
      {all.length > 0 ? (
        <dl className="flex shrink-0 flex-wrap gap-2">
          {counts.map(([label, value, dot]) => (
            <div
              key={label}
              className="flex items-center gap-2 rounded-full border border-line bg-canvas/60 py-1.5 pl-3 pr-3.5"
            >
              <span
                aria-hidden="true"
                className={`size-2 rounded-full ${value > 0 ? dot : 'bg-line-strong'}`}
              />
              <dd className="text-sm font-semibold tabular-nums text-fg">{value}</dd>
              <dt className="text-sm text-muted">{label}</dt>
            </div>
          ))}
        </dl>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// Needs attention — only what the API itself reports
// ---------------------------------------------------------------------------------------------

interface AttentionItem {
  key: string;
  icon: IconType;
  tone: 'warning' | 'danger';
  title: ReactNode;
  detail: ReactNode;
  to: string;
  linkLabel: string;
}

const ATTENTION_CONDITIONS = new Set(['overdue', 'due_today', 'due_soon']);

/**
 * Needs attention, for either view: services the API reports as needing attention, and renewal
 * terms due soon, due today or overdue. Admin also sees failing, warning or stale checks (ICT has
 * no monitoring access), and — Admin only — failing alert emails (never merely "not set up", which
 * is shown on Monitoring → Alerts). An unset Payment Account is never an attention item — it is information,
 * shown in the Payment Account panel. If any source fails, the list says it may be incomplete.
 */
export function AttentionSection({ view = 'admin' }: { view?: 'admin' | 'ict' }) {
  const canMonitoring = useHasPermission('monitoring.view') && view === 'admin';
  const canRenewals = useHasPermission('renewals.view');
  const services = useServices();
  const checks = useAllChecks(canMonitoring);
  // Open alerts (Phase 07 Stage 02): Admin only, like the checks. ICT never requests them.
  const alerts = useAlerts('open', { enabled: canMonitoring });
  // Whether the monitoring worker itself is reporting (Phase 07 Stage 03): Admin only.
  const worker = useWorkerStatus({ enabled: canMonitoring });
  // Whether alert emails are failing (Phase 07 Stage 05-C): Admin only. ICT never requests it.
  const notificationState = useNotificationState({ enabled: canMonitoring });
  const renewals = useUpcomingRenewals(null);

  const sources = [
    { name: 'Services', query: services, active: true },
    { name: 'Monitoring checks', query: checks, active: canMonitoring },
    { name: 'Open alerts', query: alerts, active: canMonitoring },
    { name: 'Monitoring worker status', query: worker, active: canMonitoring },
    { name: 'Alert email status', query: notificationState, active: canMonitoring },
    { name: 'Renewal terms', query: renewals, active: canRenewals },
  ].filter((s) => s.active);
  const loading = sources.some((s) => s.query.isPending);
  const failed = sources.filter((s) => s.query.isError);

  const byId = new Map((services.data ?? []).map((s) => [s.id, s]));
  const openAlerts = canMonitoring ? (alerts.data?.pages[0]?.items ?? []) : [];
  const moreAlerts = canMonitoring && Boolean(alerts.data?.pages[0]?.nextCursor);
  const alertOf = new Map(openAlerts.map((a) => [a.service.id, a]));
  const items: AttentionItem[] = [];

  // Monitoring itself first: when it is not reporting, nothing below can be fully trusted. It
  // never names a service as down.
  if (canMonitoring && worker.data?.state === 'not_reporting') {
    items.push({
      key: 'monitoring-worker',
      icon: PulseIcon,
      tone: 'danger',
      title: 'Monitoring is not reporting',
      detail: `Last report ${formatRelative(worker.data.lastSeenAt!)}. Service health and alerts may be out of date.`,
      to: '/app/monitoring',
      linkLabel: 'View monitoring status',
    });
  } else if (canMonitoring && worker.data?.state === 'never_reported') {
    items.push({
      key: 'monitoring-worker',
      icon: PulseIcon,
      tone: 'warning',
      title: 'Monitoring has not reported yet',
      detail:
        'No check has been run by the monitoring worker, so service health can’t be confirmed.',
      to: '/app/monitoring',
      linkLabel: 'View monitoring status',
    });
  }
  // From the deliveries' own outcomes: the most recent alert email was not accepted.
  if (canMonitoring && notificationState.data?.failing) {
    items.push({
      key: 'alert-emails',
      icon: MailIcon,
      tone: 'danger',
      title: 'Alert emails are failing',
      detail:
        'The most recent alert email was not accepted by the mail server, so Admin and ICT may not be told about disruptions.',
      to: '/app/monitoring/alerts#alert-emails',
      linkLabel: 'View alert emails',
    });
  }
  for (const service of services.data ?? []) {
    if (service.health.state !== 'needs_attention') continue;
    const alert = alertOf.get(service.id);
    items.push({
      key: `service-${service.id}`,
      icon: AlertIcon,
      tone: 'warning',
      title: `${service.name} needs attention`,
      detail: alert
        ? `${HEALTH_REASON_TEXT[service.health.reason]} Alert open since ${formatRelative(alert.openedAt)}.`
        : HEALTH_REASON_TEXT[service.health.reason],
      to: `/app/services/${service.id}`,
      linkLabel: `View ${service.name}`,
    });
  }
  for (const check of canMonitoring ? (checks.data ?? []) : []) {
    if (!check.enabled || check.lastStatus === null) continue;
    const problem = check.stale
      ? 'Its last result is out of date, so it is not current.'
      : check.lastStatus === 'ok'
        ? null
        : `${CHECK_STATUS_LABELS[check.lastStatus]}${check.lastCheckedAt ? ` · last checked ${formatRelative(check.lastCheckedAt)}` : ''}`;
    if (!problem) continue;
    items.push({
      key: `check-${check.id}`,
      icon: PulseIcon,
      tone: 'warning',
      title: `${CHECK_KIND_TITLES[check.kind]} on ${byId.get(check.serviceId)?.name ?? 'a service'}`,
      detail: problem,
      to: `/app/monitoring/${check.id}`,
      linkLabel: `View ${CHECK_KIND_TITLES[check.kind]}`,
    });
  }
  const openTerms = canRenewals ? (renewals.data?.pages.flatMap((p) => p.items) ?? []) : [];
  for (const term of openTerms) {
    if (!term.condition || !ATTENTION_CONDITIONS.has(term.condition)) continue;
    const name =
      (term.serviceId !== null ? byId.get(term.serviceId)?.name : undefined) ?? term.subjectName;
    items.push({
      key: `renewal-${term.id}`,
      icon: CalendarIcon,
      tone: term.condition === 'overdue' ? 'danger' : 'warning',
      title: `${name} renewal`,
      detail: (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span>Renewal date {formatDate(term.renewalDate)}</span>
          <RenewalStateBadge renewal={term} />
        </span>
      ),
      to: `/app/renewals/${term.id}`,
      linkLabel: `View ${name} renewal`,
    });
  }

  const nothingToReport = !loading && items.length === 0 && failed.length === 0;
  const TONE = {
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
  };

  return (
    <Panel
      title="Needs attention"
      icon={AlertIcon}
      action={
        items.length > 0 || openAlerts.length > 0 ? (
          <span className="flex items-center gap-3">
            {openAlerts.length > 0 ? (
              <ViewAllLink to="/app/monitoring/alerts">
                {`${openAlerts.length}${moreAlerts ? '+' : ''} open alert${openAlerts.length === 1 && !moreAlerts ? '' : 's'}`}
              </ViewAllLink>
            ) : null}
            {items.length > 0 ? (
              <span className="rounded-full bg-warning-soft px-2.5 py-0.5 text-xs font-semibold text-warning">
                {items.length}
              </span>
            ) : null}
          </span>
        ) : undefined
      }
    >
      <div className="space-y-3">
        {failed.map((s) => (
          <SectionError
            key={s.name}
            message={`${s.name} could not be read, so this list may be incomplete: ${s.query.error?.message ?? ''}`}
            onRetry={() => void s.query.refetch()}
          />
        ))}
        {loading ? (
          <LoadingState label="Checking what needs attention…" />
        ) : items.length > 0 ? (
          <ul aria-label="Needs attention" className="divide-y divide-line">
            {items.map((item) => {
              const ItemIcon = item.icon;
              return (
                <li
                  key={item.key}
                  className="flex items-center gap-3 py-3 text-sm first:pt-0 last:pb-0"
                >
                  <span
                    className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${TONE[item.tone]}`}
                  >
                    <ItemIcon className="size-[1.1rem]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-fg">{item.title}</p>
                    <div className="mt-0.5 text-muted">{item.detail}</div>
                  </div>
                  <Link
                    to={item.to}
                    aria-label={item.linkLabel}
                    className="shrink-0 rounded-lg border border-line-strong px-3 py-1.5 text-xs font-semibold text-fg hover:border-muted hover:bg-raised"
                  >
                    View
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : nothingToReport ? (
          services.data?.length === 0 ? (
            <p className="text-sm text-muted">
              No services have been added yet, so nothing is being checked.
            </p>
          ) : (
            <div className="flex items-center gap-3 rounded-xl bg-success-soft/60 px-4 py-3 text-sm">
              <CheckCircleIcon className="size-5 shrink-0 text-success" />
              <p className="text-fg-2">
                <span className="font-semibold text-fg">Nothing needs attention right now.</span> No
                service or renewal is reporting a problem.
              </p>
            </div>
          )
        ) : null}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------
// Your services — the four service objects, health exactly as the API derived it
// ---------------------------------------------------------------------------------------------

/** What each service is, in plain words (no provider or software names). */
const SERVICE_DESCRIPTIONS: Record<Service['type'], string> = {
  cloud_server: 'Files, documents and team collaboration',
  mail_server: 'Company email',
  storage_bucket: 'Storage for files and backups',
  website: "Bekebi's public website",
};

export function ServicesSection() {
  const services = useServices();
  const canAdd = useHasPermission('services.manage');
  const all = services.data ?? [];
  const ordered = SERVICE_TYPES.flatMap((type) => all.filter((s) => s.type === type));
  const missing = SERVICE_TYPES.filter((type) => !all.some((s) => s.type === type));

  return (
    <Panel
      title="Your services"
      icon={ServicesIcon}
      action={<ViewAllLink to="/app/services">All services</ViewAllLink>}
    >
      {services.isPending ? (
        <LoadingState label="Loading services…" />
      ) : services.isError ? (
        <SectionError
          message={`Services could not be loaded: ${services.error.message}`}
          onRetry={() => void services.refetch()}
        />
      ) : (
        <div className="space-y-3">
          {ordered.length > 0 ? (
            <ul aria-label="Services" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {ordered.map((service) => (
                <li key={service.id}>
                  <ServiceCard service={service} />
                </li>
              ))}
            </ul>
          ) : null}
          {missing.length > 0 ? (
            ordered.length === 0 ? (
              <PanelEmpty
                icon={ServicesIcon}
                title="No services yet"
                action={
                  canAdd ? (
                    <Link
                      to="/app/services/new"
                      className="inline-flex min-h-9 items-center rounded-lg bg-accent px-3.5 text-sm font-semibold text-on-accent hover:bg-accent-hover"
                    >
                      Add a service
                    </Link>
                  ) : undefined
                }
              >
                Not added to the portal yet: {missing.map((t) => SERVICE_TYPE_LABELS[t]).join(', ')}
                . Their state is not known.
              </PanelEmpty>
            ) : (
              <p className="text-sm text-muted">
                Not added to the portal yet: {missing.map((t) => SERVICE_TYPE_LABELS[t]).join(', ')}
                . Their state is not known.
              </p>
            )
          ) : null}
        </div>
      )}
    </Panel>
  );
}

function renewalLine(renewal: Service['renewal']): string {
  if (!renewal) return 'No renewal date recorded';
  if (renewal.countdown.kind === 'overdue')
    return `Renewal ${renewal.countdown.label.toLowerCase()}`;
  if (renewal.countdown.kind === 'due_today') return 'Renews today';
  return `Renews in ${renewal.countdown.label} · ${formatDate(renewal.renewalDate)}`;
}

function ServiceCard({ service }: { service: Service }) {
  if (!hasServiceHealth(service.type)) return <StorageServiceCard service={service} />;
  const checked = service.health.checkedAt;
  const overdue = service.renewal?.countdown.kind === 'overdue';
  return (
    <article className="group relative flex h-full gap-3 rounded-xl border border-line bg-canvas/40 p-4 transition-colors hover:border-line-strong hover:bg-raised/40 sm:flex-col sm:gap-0 sm:p-5">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <ServiceTypeIcon type={service.type} />
        <span className="hidden sm:inline-flex">
          <HealthBadge health={service.health} />
        </span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-2 sm:mt-4">
          <h3 className="text-base font-semibold">
            <Link
              to={`/app/services/${service.id}`}
              className="text-fg after:absolute after:inset-0 after:rounded-xl group-hover:text-accent-link"
            >
              {service.name}
            </Link>
          </h3>
          <span className="sm:hidden">
            <HealthBadge health={service.health} />
          </span>
        </div>
        <p className="mt-0.5 break-words text-sm text-muted">
          {service.type === 'website' && service.accessUrl
            ? service.accessUrl
            : SERVICE_DESCRIPTIONS[service.type]}
        </p>
        <p className="mb-3 mt-2 text-sm text-fg-2 sm:mb-4 sm:mt-3">
          {HEALTH_REASON_TEXT[service.health.reason]}
        </p>
        <div className="mt-auto space-y-1.5 border-t border-line pt-3 text-xs text-muted">
          <p className="flex items-center gap-2">
            <PulseIcon className="size-3.5 shrink-0" />
            {checked ? (
              <span title={formatDateTime(checked)}>Last checked {formatRelative(checked)}</span>
            ) : (
              'No check result yet'
            )}
          </p>
          <p className={`flex items-center gap-2 ${overdue ? 'text-danger' : ''}`}>
            <CalendarIcon className="size-3.5 shrink-0" />
            {renewalLine(service.renewal)}
          </p>
        </div>
      </div>
    </article>
  );
}

/**
 * The Storage Bucket's card: no health state (director, 2026-11-02). Its space as the storage
 * dashboard writes it ("360 GB / 2 TB (17.6%)") with what is left, then its renewal.
 */
function StorageServiceCard({ service }: { service: Service }) {
  const metrics = useServiceMetrics(service.id);
  const used = metrics.data?.metrics.find((m) => m.key === 'storage.bucket.used_bytes');
  const plan = metrics.data?.metrics.find((m) => m.key === 'storage.bucket.capacity_bytes');
  const overdue = service.renewal?.countdown.kind === 'overdue';
  const usedPercent = used && plan ? storagePercent(used.value, plan.value) : null;
  const width = usedPercent === null ? 0 : Math.min(100, Number(usedPercent));
  return (
    <article className="group relative flex h-full gap-3 rounded-xl border border-line bg-canvas/40 p-4 transition-colors hover:border-line-strong hover:bg-raised/40 sm:flex-col sm:gap-0 sm:p-5">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <ServiceTypeIcon type={service.type} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <h3 className="text-base font-semibold sm:mt-4">
          <Link
            to={`/app/services/${service.id}`}
            className="text-fg after:absolute after:inset-0 after:rounded-xl group-hover:text-accent-link"
          >
            {service.name}
          </Link>
        </h3>
        <p className="mt-0.5 break-words text-sm text-muted">
          {SERVICE_DESCRIPTIONS[service.type]}
        </p>
        <div className="mb-3 mt-2 space-y-1.5 sm:mb-4 sm:mt-3">
          {used && plan && usedPercent !== null ? (
            <>
              <div
                role="meter"
                aria-label="Storage Bucket space used"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={width}
                className="h-1.5 w-full overflow-hidden rounded-full bg-raised"
              >
                <div
                  className={`h-full rounded-full ${width >= 90 ? 'bg-warning' : 'bg-success'}`}
                  style={{ width: `${width}%` }}
                />
              </div>
              <p className="text-sm text-fg-2">
                {formatStorageSize(used.value)} / {formatStorageSize(plan.value)} ({usedPercent}%)
              </p>
              <p className="text-xs text-muted">
                {storagePercent(used.value, plan.value, true)}% left
              </p>
            </>
          ) : (
            <p className="text-sm text-fg-2">
              {used ? `${formatStorageSize(used.value)} used` : 'Space used not measured yet'}
            </p>
          )}
        </div>
        <div className="mt-auto space-y-1.5 border-t border-line pt-3 text-xs text-muted">
          <p className="flex items-center gap-2">
            <PulseIcon className="size-3.5 shrink-0" />
            {used ? (
              <span title={formatDateTime(used.observedAt)}>
                Measured {formatRelative(used.observedAt)}
              </span>
            ) : (
              'Not measured yet'
            )}
          </p>
          <p className={`flex items-center gap-2 ${overdue ? 'text-danger' : ''}`}>
            <CalendarIcon className="size-3.5 shrink-0" />
            {renewalLine(service.renewal)}
          </p>
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------------------------
// Renewals — open terms, soonest first (the API's order, condition and countdown)
// ---------------------------------------------------------------------------------------------

const RENEWALS_SHOWN = 4;
const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' });

function DateBlock({ ymd }: { ymd: string }) {
  const date = new Date(`${ymd}T00:00:00Z`);
  return (
    <span
      aria-hidden="true"
      className="flex w-11 shrink-0 flex-col items-center rounded-lg border border-line bg-canvas/60 py-1"
    >
      <span className="text-[0.65rem] font-semibold uppercase text-muted">
        {MONTH.format(date)}
      </span>
      <span className="text-base font-semibold leading-tight text-fg">{date.getUTCDate()}</span>
    </span>
  );
}

export function RenewalsSection() {
  const canRenewals = useHasPermission('renewals.view');
  const renewals = useUpcomingRenewals(null);
  const services = useServices();
  if (!canRenewals) return null;
  const byId = new Map((services.data ?? []).map((s) => [s.id, s]));
  const terms: Renewal[] = renewals.data?.pages.flatMap((p) => p.items) ?? [];
  const withoutTerm = (services.data ?? []).filter((s) => s.renewal === null);

  return (
    <Panel
      title="Renewals"
      icon={CalendarIcon}
      action={<ViewAllLink to="/app/renewals">All renewals</ViewAllLink>}
    >
      {renewals.isPending ? (
        <LoadingState label="Loading renewals…" />
      ) : renewals.isError ? (
        <SectionError
          message={`Renewal terms could not be loaded: ${renewals.error.message}`}
          onRetry={() => void renewals.refetch()}
        />
      ) : terms.length === 0 ? (
        <PanelEmpty icon={CalendarIcon} title="No renewals yet">
          Renewal information will appear here when a service has a renewal date.
        </PanelEmpty>
      ) : (
        <div className="space-y-3">
          <ul aria-label="Open renewal terms" className="space-y-2">
            {terms.slice(0, RENEWALS_SHOWN).map((term) => (
              <li
                key={term.id}
                className="flex items-center gap-3 rounded-xl border border-line bg-canvas/40 p-3 text-sm"
              >
                <DateBlock ymd={term.renewalDate} />
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/app/renewals/${term.id}`}
                    className="font-semibold text-fg hover:text-accent-link hover:underline hover:underline-offset-4"
                  >
                    {(term.serviceId !== null ? byId.get(term.serviceId)?.name : undefined) ??
                      term.subjectName}
                  </Link>
                  <span className="sr-only"> renews {formatDate(term.renewalDate)}</span>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <RenewalStateBadge renewal={term} />
                    <span className="text-xs text-muted">
                      {formatMoney(term.price.amount, term.price.currency)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {terms.length > RENEWALS_SHOWN || renewals.hasNextPage ? (
            <p className="text-xs text-muted">More renewals are on the Renewals page.</p>
          ) : null}
          {services.isSuccess && withoutTerm.length > 0 ? (
            <p className="text-xs text-muted">
              No renewal date is recorded for {names(withoutTerm)}.
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------
// Recent activity — the newest audit entries, as recorded
// ---------------------------------------------------------------------------------------------

const ACTIVITY_SHOWN = 5;

export function ActivityRow({
  title,
  who,
  occurredAt,
  children,
}: {
  title: string;
  who?: string;
  occurredAt: string;
  children?: ReactNode;
}) {
  return (
    <li className="relative pl-5 text-sm">
      <span aria-hidden="true" className="absolute left-0 top-1.5 size-2 rounded-full bg-accent" />
      <p className="font-medium text-fg">{title}</p>
      <p className="break-all text-xs text-muted">
        {who ? <>{who} · </> : null}
        <time dateTime={occurredAt} title={formatDateTime(occurredAt)}>
          {formatRelative(occurredAt)}
        </time>
      </p>
      {children}
    </li>
  );
}

function RecentActivitySection() {
  const canAudit = useHasPermission('audit.view');
  const activity = useActivity();
  const users = useUserDirectory(useHasPermission('users.manage'));
  if (!canAudit) return null;
  const byId = new Map((users.data ?? []).map((u) => [u.id, u]));
  const entries = (activity.data?.pages[0]?.items ?? []).slice(0, ACTIVITY_SHOWN);

  return (
    <Panel
      title="Recent activity"
      icon={ListIcon}
      action={<ViewAllLink to="/app/activity">All activity</ViewAllLink>}
    >
      {activity.isPending ? (
        <LoadingState label="Loading activity…" />
      ) : activity.isError ? (
        <SectionError
          message={`Activity could not be loaded: ${activity.error.message}`}
          onRetry={() => void activity.refetch()}
        />
      ) : entries.length === 0 ? (
        <PanelEmpty icon={ListIcon} title="No activity yet">
          Actions taken in the portal will appear here.
        </PanelEmpty>
      ) : (
        <ol aria-label="Recent activity" className="space-y-3.5">
          {entries.map((entry) => {
            const actor = entry.actorId ? byId.get(entry.actorId) : undefined;
            return (
              <ActivityRow
                key={entry.id}
                title={actionLabel(entry.action)}
                who={actor ? actor.email : actorKindLabel(entry.actorType)}
                occurredAt={entry.occurredAt}
              />
            );
          })}
        </ol>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------
// Payment Account — information, never an attention item
// ---------------------------------------------------------------------------------------------

/**
 * Whether the Payment Account is set, as information. ICT sees the details (approved, read-only).
 * The Admin Dashboard shows only whether it is set — the details stay on the Payment Account page.
 */
export function PaymentAccountPanel({ view }: { view: 'admin' | 'ict' }) {
  const account = usePaymentAccount();
  const canManage = useHasPermission('payment_account.manage');

  return (
    <Panel
      title="Payment Account"
      icon={BankIcon}
      action={
        <ViewAllLink to="/app/payment-account" short="Open">
          Open Payment Account
        </ViewAllLink>
      }
    >
      {account.isPending ? (
        <LoadingState label="Loading the Payment Account…" />
      ) : account.isError ? (
        <SectionError
          message={`The Payment Account could not be loaded: ${account.error.message}`}
          onRetry={() => void account.refetch()}
        />
      ) : account.data === null ? (
        <PanelEmpty icon={BankIcon} title="Payment Account not set">
          Not set by the Admin yet.
          {canManage ? (
            <>
              {' '}
              <Link to="/app/payment-account" className={linkClass}>
                Set it now
              </Link>
            </>
          ) : null}
        </PanelEmpty>
      ) : view === 'admin' ? (
        <div className="flex items-center gap-3 rounded-xl border border-line bg-canvas/40 p-4 text-sm">
          <CheckCircleIcon className="size-5 shrink-0 text-success" />
          <div>
            <p className="font-semibold text-fg">Payment Account is set</p>
            <p className="text-xs text-muted">
              Last changed {formatRelative(account.data.updatedAt)}
            </p>
          </div>
        </div>
      ) : (
        <dl className="space-y-3 text-sm">
          {(
            [
              ['Account name', account.data.bankAccountName, ''],
              ['Bank', account.data.bankName, ''],
              ['Account number', account.data.bankAccountNumber, 'font-mono tracking-wide'],
              ['Last changed', formatDateTime(account.data.updatedAt), ''],
            ] as const
          ).map(([label, value, extra]) => (
            <div key={label}>
              <dt className="text-xs text-muted">{label}</dt>
              <dd className={`break-words font-medium text-fg ${extra}`}>{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------
// Not connected yet — what the portal cannot see yet (never shown as figures)
// ---------------------------------------------------------------------------------------------

function NotConnectedTile({
  icon: TileIcon,
  title,
  children,
}: {
  icon: IconType;
  title: string;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-3 rounded-xl border border-line bg-canvas/40 p-4">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-raised text-muted">
        <TileIcon className="size-[1.1rem]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-fg">{title}</p>
          <span className="mt-0.5 shrink-0 whitespace-nowrap rounded-full border border-line-strong px-2 py-px text-[0.62rem] font-semibold uppercase tracking-wide text-muted">
            Not connected
          </span>
        </div>
        <p className="mt-1 text-muted">{children}</p>
      </div>
    </li>
  );
}

/**
 * Mail Server backups at a glance (A-M6): whether a daily backup ARRIVED in the Storage Bucket on
 * schedule — not whether it is complete or restorable. Admin and ICT (CLAUDE.md §4).
 */
export function BackupsPanel() {
  const status = useBackupStatus();
  const services = useServices();
  const mail = services.data?.find((s) => s.type === 'mail_server');
  return (
    <Panel
      title="Backups"
      icon={HistoryIcon}
      action={
        mail ? <ViewAllLink to={`/app/services/${mail.id}`}>Open Mail Server</ViewAllLink> : null
      }
    >
      {status.isPending ? (
        <LoadingState label="Loading backup status…" />
      ) : status.isError ? (
        <SectionError
          message="Backup status could not be loaded."
          onRetry={() => void status.refetch()}
        />
      ) : (
        <div className="space-y-2 text-sm">
          <BackupStateBadge state={status.data.state} />
          <p className="text-fg">{backupStateSentence(status.data)}</p>
          {status.data.latestDaily && status.data.lastCheckedAt !== null ? (
            <p className="text-muted">
              Newest: {status.data.latestDaily.backupDate},{' '}
              {formatBytes(status.data.latestDaily.sizeBytes)}. Arrival only — not proof the backup
              can be restored.
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

export function NotConnectedSection({ view = 'admin' }: { view?: 'admin' | 'ict' }) {
  // Tiles leave this list once real readings exist — a connected source is never shown as
  // "not connected" (and nothing is shown as connected without a reading).
  const services = useServices();
  const cloud = services.data?.find((s) => s.type === 'cloud_server');
  const bucket = services.data?.find((s) => s.type === 'storage_bucket');
  const cloudMetrics = useServiceMetrics(cloud?.id ?? '', cloud !== undefined);
  const bucketMetrics = useServiceMetrics(bucket?.id ?? '', bucket !== undefined);
  const serverResourcesConnected = (cloudMetrics.data?.metrics.length ?? 0) > 0;
  const storageConnected = (bucketMetrics.data?.metrics.length ?? 0) > 0;
  const canMail = useHasPermission('mail.mailboxes.view');
  const mailbox = useMailboxCapability(canMail);
  // Once the Mail Server answers, mailboxes are connected: they leave this list.
  const mailText = !canMail
    ? null
    : !mailbox.data
      ? 'Checking whether mailbox management is connected…'
      : mailbox.data.capability === 'available'
        ? null
        : MAILBOX_CAPABILITY_TEXT[mailbox.data.capability];

  return (
    <Panel title="Not connected yet" icon={PlugIcon}>
      <p className="-mt-1 mb-4 text-sm text-muted">
        These aren&rsquo;t connected to the portal yet, so no figures are shown for them.
      </p>
      <ul
        aria-label="Not connected yet"
        className="grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-3"
      >
        {serverResourcesConnected ? null : (
          <NotConnectedTile icon={ServicesIcon} title="Server resources">
            CPU, memory and disk use for the Cloud Server isn&rsquo;t connected yet.
          </NotConnectedTile>
        )}
        {storageConnected ? null : (
          <NotConnectedTile icon={BucketIcon} title="Storage space">
            How much space the Storage Bucket is using isn&rsquo;t connected yet.
          </NotConnectedTile>
        )}
        {mailText ? (
          <NotConnectedTile icon={MailIcon} title="Mailboxes">
            {mailText}
          </NotConnectedTile>
        ) : null}
        {view === 'admin' ? (
          <NotConnectedTile icon={LayersIcon} title="Live resource information">
            Basic infrastructure details are available, but live resource information isn&rsquo;t
            connected yet.{' '}
            <Link to="/app/infrastructure" className={linkClass}>
              View infrastructure
            </Link>
          </NotConnectedTile>
        ) : (
          <NotConnectedTile icon={LayersIcon} title="Live resource information">
            Live resource information from Bekebi&rsquo;s service providers isn&rsquo;t connected
            yet.
          </NotConnectedTile>
        )}
      </ul>
    </Panel>
  );
}
